import { Amplify } from 'aws-amplify';

import {
  getAppSyncEndpoint,
  getAwsRegion,
  getCognitoUserPoolId,
  getCognitoClientId,
  getBaseUrl,
  getIdentityPoolId,
  getCognitoDomain,
  getAppSyncApiKey,
} from './environment';
import {
  AmplifyAuthConfig,
  AmplifyAppSyncConfig,
  UseAmplifyInitOptions,
} from '../hooks/useAmplifyInit';

/**
 * Builds an Amplify Auth configuration based on environment variables
 * @returns The Amplify Auth configuration
 */
export const buildAuthConfig = (isWeb: boolean): AmplifyAuthConfig => {
  const userPoolId = getCognitoUserPoolId();
  const clientId = getCognitoClientId();
  const baseUrl = getBaseUrl(isWeb);
  const identityPoolId = getIdentityPoolId();
  const domain = getCognitoDomain();
  let redirectSignIn: string[] = [];
  let redirectSignOut: string[] = [];

  if (isWeb) {
    const webRedirectUri = `${baseUrl}/oauth-callback`;
    redirectSignIn = [webRedirectUri];
    redirectSignOut = [webRedirectUri];

    if (typeof window !== 'undefined') {
      const currentUrl = window.location.origin + '/oauth-callback';
      if (!redirectSignIn.includes(currentUrl)) {
        redirectSignIn.push(currentUrl);
      }
      if (!redirectSignOut.includes(currentUrl)) {
        redirectSignOut.push(currentUrl);
      }
    }
  } else {
    redirectSignIn = ['heartbeat://callback'];
    redirectSignOut = ['heartbeat://signout'];
  }

  return {
    userPoolClientId: clientId,
    userPoolId,
    identityPoolId,
    region: getAwsRegion(),
    oauth: {
      domain,
      scopes: ['aws.cognito.signin.user.admin', 'email', 'openid', 'phone', 'profile'],
      redirectSignIn,
      redirectSignOut,
      responseType: 'code',
      providers: ['Google', 'Apple', 'Microsoft'],
    },
  };
};

/**
 * Builds an Amplify AppSync configuration based on environment variables
 * @param authenticationType Optional authentication type override
 * @returns The Amplify AppSync configuration
 */
export const buildAppSyncConfig = (
  authenticationType: 'userPool' | 'apiKey' = 'userPool',
): AmplifyAppSyncConfig => ({
  graphqlEndpoint: getAppSyncEndpoint(),
  region: getAwsRegion(),
  authenticationType,
  apiKey: authenticationType === 'apiKey' ? getAppSyncApiKey() : undefined,
});

/**
 * Builds a complete Amplify configuration based on environment variables
 * @param options Optional overrides for the configuration
 * @returns The complete Amplify configuration
 */
export const buildAmplifyConfig = (
  options?: Partial<UseAmplifyInitOptions>,
): UseAmplifyInitOptions => {
  const isWeb = options?.isWeb === true;
  const authType = options?.appSync?.authenticationType;
  const authenticationType: 'userPool' | 'apiKey' = authType === 'apiKey' ? 'apiKey' : 'userPool';
  const defaultConfig: UseAmplifyInitOptions = {
    auth: buildAuthConfig(isWeb),
    appSync: buildAppSyncConfig(authenticationType),
    isWeb,
  };

  if (!defaultConfig.appSync?.graphqlEndpoint) {
    defaultConfig.appSync = undefined;
  }

  return {
    ...defaultConfig,
    ...options,
    auth: options?.auth ? { ...defaultConfig.auth, ...options.auth } : defaultConfig.auth,
    appSync:
      options?.appSync && defaultConfig.appSync
        ? { ...defaultConfig.appSync, ...options.appSync }
        : options?.appSync || defaultConfig.appSync,
    isWeb,
  };
};

/**
 * Configures Amplify globally with the provided configuration
 * This ensures we only have one configured instance across the application
 * @param options Optional configuration options
 * @returns The configured Amplify instance
 */
export const configureAmplify = (options?: Partial<UseAmplifyInitOptions>): typeof Amplify => {
  const config = buildAmplifyConfig(options);
  const amplifyConfig: any = {
    Auth: {
      Cognito: {
        userPoolId: config.auth?.userPoolId,
        userPoolClientId: config.auth?.userPoolClientId,
        identityPoolId: config.auth?.identityPoolId,
        loginWith: {
          oauth: {
            domain: config.auth?.oauth?.domain,
            scopes: config.auth?.oauth?.scopes,
            redirectSignIn: config.auth?.oauth?.redirectSignIn,
            redirectSignOut: config.auth?.oauth?.redirectSignOut,
            responseType: config.auth?.oauth?.responseType,
          },
        },
      },
    },
  };

  if (config.appSync?.graphqlEndpoint) {
    amplifyConfig.API = {
      GraphQL: {
        endpoint: config.appSync.graphqlEndpoint,
        apiKey: config.appSync.apiKey,
        region: config.appSync.region || config.auth?.region,
        defaultAuthMode: config.appSync.authenticationType,
      },
    };

    if (config.appSync.graphqlEndpoint) {
      const wsEndpoint = config.appSync.graphqlEndpoint.replace('https://', 'wss://');
      amplifyConfig.API.GraphQL.websocket = {
        url: wsEndpoint,
        keepAlive: 300000, // 5 minutes
      };
    }
  }

  Amplify.configure(amplifyConfig);
  return Amplify;
};
